import LZString from "lz-string";

type StorageParamsType = {
  type: "localStorage" | "sessionStorage";
  /* key identifier for the data stored by this instance */
  dataIdentifier: string;
  /* data to be compressed by lz-string */
  toBeCompressed?: boolean;
  /* Hours to cache a single value */
  hoursToCache?: number;
};

type StorageDataType = {
  dataString: string;
  timestamp: number
}

// 1000ms * 60s * 60m
const hourInMilliseconds = 3600000;

export class BrowserStorage<T> {
  private readonly storage: Storage | null;
  private readonly dataIdentifier: string;
  private readonly toBeCompressed: boolean;
  private readonly cacheDuration: number;

  public static isStorageTypeSupported(type: "localStorage" | "sessionStorage"): boolean {
    try {
      // throws an error if storage type is not supported or if disabled by user
      // Also test some special characters in key and value
      const storage = window[type];
      const key = "t?e=s&t";
      storage.setItem(key, key);
      storage.getItem(key);
      storage.removeItem(key);

      return true;
    } catch (e) {
      return false;
    }
  }

  constructor(storageParams: StorageParamsType) {
    if (BrowserStorage.isStorageTypeSupported(storageParams.type)) {
      this.storage = window[storageParams.type];
      this.dataIdentifier = storageParams.dataIdentifier;
      this.toBeCompressed = !!storageParams.toBeCompressed;
      this.cacheDuration = (storageParams.hoursToCache ?? 0) * hourInMilliseconds;
    } else {
      this.storage = null;
      this.dataIdentifier = "null";
      this.toBeCompressed = false;
      this.cacheDuration = 0;
    }
  }

  private key(storageEntryKey: string | number): string {
    if (storageEntryKey.toString().startsWith(this.dataIdentifier)) {
      return storageEntryKey.toString();
    }

    return `${this.dataIdentifier}-${storageEntryKey.toString()}`;
  }

  clear = (): void => {
    if (this.storage) this.storage.clear();
  };

  write = (storageEntryKey: number | string, data: T): void => {
    if (this.storage) {
      let stringifiedData = JSON.stringify(data);

      if (this.toBeCompressed) {
        stringifiedData = LZString.compressToUTF16(stringifiedData);
      }

      let timestamp = 0;
      if (this.cacheDuration) {
        timestamp = Date.now() + this.cacheDuration;
      }

      const timestamped = JSON.stringify({
        dataString: stringifiedData,
        timestamp,
      });

      this.storage.setItem(this.key(storageEntryKey), timestamped);
    }
  };

  read = (key: string): T | null => {
    if (!this.storage) {
      return null;
    }
    try {
      const entry = this.storage.getItem(this.key(key));
      if (!entry) {
        return null;
      }
      const timestampedStorageContent = JSON.parse(entry) as StorageDataType;

      if (timestampedStorageContent.timestamp && timestampedStorageContent.timestamp < Date.now()) {
        this.remove(key);

        return null;
      }

      let storageContent = timestampedStorageContent.dataString;
      if (this.toBeCompressed) {
        storageContent = LZString.decompressFromUTF16(storageContent) ?? "";
      }

      return JSON.parse(storageContent) as T;
    } catch (e) {
      return null;
    }
  };

  remove = (key: string): void => {
    if (this.storage) this.storage.removeItem(this.key(key));
  };
}
